import { databases } from '../lib/appwrite';
import { ID, Permission, Role } from 'appwrite';

// ============================================
// APPWRITE VENDOR APPLICATION SERVICE
// ============================================

const DATABASE_ID = process.env.REACT_APP_APPWRITE_DATABASE_ID;
const VENDOR_APPLICATION_COLLECTION_ID = process.env.REACT_APP_APPWRITE_VENDORAPPLICATION_COLLECTION_ID;

console.log('Vendor Application Service Config:', {
  DATABASE_ID,
  VENDOR_APPLICATION_COLLECTION_ID
});

/**
 * Check whether a vendor name is already taken by pending or approved applications
 * @param {string} vendorName - The vendor display name to check
 * @param {string|null} excludeProfileId - Optional profile id to exclude from the check (useful when re-checking own name)
 * @returns {Promise<boolean>} - True when name is taken
 */
export const isVendorNameTaken = async (vendorName, excludeProfileId = null) => {
  try {
    if (!vendorName) return false;

    if (!DATABASE_ID || !VENDOR_APPLICATION_COLLECTION_ID) {
      console.warn('Database configuration missing for vendor name check');
      return false;
    }

    const { Query } = require('appwrite');

    // Use a search query to find likely matches, then perform a strict case-insensitive check in JS
    const response = await databases.listDocuments(
      DATABASE_ID,
      VENDOR_APPLICATION_COLLECTION_ID,
      [
        Query.search('vendor_name', vendorName),
        Query.limit(10)
      ]
    );

    const normalized = vendorName.trim().toLowerCase();

    return response.documents.some(doc => {
      if (!doc.vendor_name) return false;
      if (excludeProfileId && doc.userprofile_id === excludeProfileId) return false;
      const status = (doc.status || '').toLowerCase();
      return (status === 'pending' || status === 'approved') && doc.vendor_name.trim().toLowerCase() === normalized;
    });
  } catch (error) {
    console.error('Vendor name check error:', error);
    return false;
  }
};

/**
 * Create a vendor application document
 * @param {Object} payload - Application payload
 * @param {string} payload.userprofile_id - FK to Profiles collection (profile document id)
 * @param {string} payload.full_name - Applicant full name
 * @param {string} payload.vendor_name - Vendor name to display to customers
 * @param {string} payload.contact_email - Contact email
 * @param {string} payload.whatsapp_number - Contact WhatsApp number
 * @param {string} payload.status - Application status (pending/approved/rejected)
 * @param {string|null} opts.userId - Appwrite system user id (for permissions)
 */
export const createVendorApplication = async (
  {
    userprofile_id,
    full_name,
    vendor_name,
    contact_email,
    whatsapp_number,
    status = 'pending'
  },
  { userId = null } = {}
) => {

  try {
    if (!DATABASE_ID || !VENDOR_APPLICATION_COLLECTION_ID) {
      throw new Error('Appwrite database or vendor application collection ID not configured');
    }

    // Ensure vendor_name is unique among pending and approved applications (exclude same profile)
    if (vendor_name) {
      const taken = await isVendorNameTaken(vendor_name, userprofile_id);
      if (taken) {
        throw new Error('Vendor name is already taken. Please choose a different display name.');
      }
    }

    const permissions = [];

    // If we have an authenticated user id, grant them read/update/delete on their application
    if (userId) {
      permissions.push(Permission.read(Role.user(userId)));
      permissions.push(Permission.update(Role.user(userId)));
      permissions.push(Permission.delete(Role.user(userId)));
    }

    const doc = await databases.createDocument(
      DATABASE_ID,
      VENDOR_APPLICATION_COLLECTION_ID,
      ID.unique(),
      {
        userprofile_id,
        full_name,
        vendor_name,
        contact_email,
        whatsapp_number,
        status
      },
      permissions
    );

    return doc;
  } catch (error) {
    console.error('Create vendor application error:', error);
    throw error;
  }
};

/**
 * Get vendor application by profile ID
 * @param {string} profileId - The user's profile ID
 * @returns {Promise<Object|null>} - The application document or null if not found
 */
export const getVendorApplicationByProfileId = async (profileId) => {
  try {
    if (!profileId) return null;

    if (!DATABASE_ID || !VENDOR_APPLICATION_COLLECTION_ID) {
      console.warn('Database configuration missing for vendor application check');
      return null;
    }

    const { Query } = require('appwrite');

    // We want to find *any* application for this profile that is NOT rejected?
    // Or just the most recent one? 
    // The requirement says "check if an application has been submitted before".
    // Usually we care if there is a pending or approved one. 
    // If they were rejected, maybe they can apply again? 
    // User request: "check if an application has been submitted before... show that their application is pending"
    // Let's fetch the most recent one.

    const response = await databases.listDocuments(
      DATABASE_ID,
      VENDOR_APPLICATION_COLLECTION_ID,
      [
        Query.equal('userprofile_id', profileId),
        Query.orderDesc('$createdAt'),
        Query.limit(1)
      ]
    );

    if (response.documents.length > 0) {
      return response.documents[0];
    }

    return null;
  } catch (error) {
    console.error('Get vendor application error:', error);
    return null;
  }
};
